import { useCallback, useEffect, useRef, useState } from "react";
import { COUNTDOWN_SEC } from "../../convex/lib/game";
import { useDubActions } from "../components/backend";
import { Badge } from "../components/Badge";
import { Button } from "../components/Button";
import type { Assignment, Auth, RecordStage, ToastTone } from "../components/contracts";
import { CountdownOverlay } from "../components/CountdownOverlay";
import { Footer } from "../components/Footer";
import { HostAbsentNotice } from "../components/HostAbsentNotice";
import { useMedia, useMicLevel } from "../components/media";
import { MicLevel } from "../components/MicLevel";
import { NavBar } from "../components/NavBar";
import { Panel } from "../components/Panel";
import { cx, errorText } from "../components/util";

export interface RecordProps {
  auth: Auth;
  round: number;
  assignment: Assignment;
  /** Host, OR anyone once the host has gone quiet. */
  canControl: boolean;
  /** True only in the widened case, so the extra button gets explained. */
  hostAbsent: boolean;
  /** `rounds.advancePhase` — recording into playback. */
  onAdvance: () => Promise<void>;
  onToast: (text: string, tone?: ToastTone) => void;
}

const BANNER: Record<RecordStage, { text: string; tone: string }> = {
  idle: { text: "Get ready", tone: "" },
  refresher: { text: "Watch — this is the bit", tone: "is-refresher" },
  countdown: { text: "Get ready", tone: "is-countdown" },
  recording: { text: "Recording — talk!", tone: "is-recording" },
  done: { text: "Segment done", tone: "is-done" },
};

export function Record({
  auth,
  round,
  assignment,
  canControl,
  hostAbsent,
  onAdvance,
  onToast,
}: RecordProps) {
  const [advancing, setAdvancing] = useState(false);

  async function goPlayback() {
    setAdvancing(true);
    try {
      await onAdvance();
    } catch (err) {
      setAdvancing(false);
      onToast(errorText(err, "Couldn't start playback."), "error");
    }
  }

  const media = useMedia();
  const dubs = useDubActions();
  const videoRef = useRef<HTMLVideoElement>(null);

  const [stage, setStage] = useState<RecordStage>("idle");
  const [index, setIndex] = useState(0);
  const [count, setCount] = useState<number | null>(null);
  const [recorded, setRecorded] = useState<number[]>(assignment.recorded);
  const [pickingRedo, setPickingRedo] = useState(false);
  const [armed, setArmed] = useState(false);

  /** When set, we are re-recording exactly one chunk and returning to "done". */
  const redoTarget = useRef<number | null>(null);

  const chunks = assignment.subChunks;
  const chunk = chunks[index];
  const level = useMicLevel(armed);

  const advance = useCallback(() => {
    if (redoTarget.current !== null) {
      redoTarget.current = null;
      setStage("done");
      return;
    }
    if (index + 1 < chunks.length) {
      setIndex(index + 1);
      setStage("refresher");
    } else {
      setStage("done");
    }
  }, [index, chunks.length]);

  // --- Stage 1: refresher pass, ORIGINAL AUDIO ON --------------------------
  useEffect(() => {
    const video = videoRef.current;
    if (stage !== "refresher" || !video || !chunk) return;

    let cancelled = false;
    void (async () => {
      try {
        // Seeks and waits for the frame to land before unmuting, so the player
        // hears the sub-chunk from its real start. The adapter re-mutes on the
        // way out however this ends.
        await media.playRefresher(video, chunk);
        if (!cancelled) setStage("countdown");
      } catch {
        if (!cancelled) {
          onToast("Couldn't play that clip back. Tap the video, then retry.", "error");
          setStage("idle");
        }
      }
    })();

    return () => {
      cancelled = true;
      video.pause();
      video.muted = true;
    };
    // Index, not the object — see the recording effect below for why a fresh
    // `chunk` reference on every render would restart this pass repeatedly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, chunk?.index]);

  // --- Stage 2: visual countdown, no audio cue -----------------------------
  useEffect(() => {
    if (stage !== "countdown") {
      setCount(null);
      return;
    }
    setCount(COUNTDOWN_SEC);
    let remaining = COUNTDOWN_SEC;
    const id = window.setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) {
        window.clearInterval(id);
        setCount(null);
        setStage("recording");
      } else {
        setCount(remaining);
      }
    }, 1000);
    return () => window.clearInterval(id);
  }, [stage]);

  // --- Stage 3: muted replay + auto-start recording ------------------------
  useEffect(() => {
    const video = videoRef.current;
    if (stage !== "recording" || !video || !chunk) return;

    let cancelled = false;

    void (async () => {
      try {
        // Muted replay + capture, bounded by the sub-chunk actually playing.
        const blob = await media.recordChunk(video, chunk);
        if (cancelled) return;
        video.pause();

        const { r2Key, uploadUrl } = await dubs.request(
          auth,
          round,
          chunk.index,
          blob.type || "audio/webm",
        );
        await media.upload(blob, uploadUrl, () => {});
        await dubs.submit(auth, round, chunk.index, r2Key);

        if (cancelled) return;
        setRecorded((prev) => (prev.includes(chunk.index) ? prev : [...prev, chunk.index]));
        advance();
      } catch (err) {
        if (cancelled) return;
        onToast(errorText(err, "That take didn't save."), "error");
        setStage("done");
      }
    })();

    return () => {
      cancelled = true;
      video.pause();
    };
    // Depend on the sub-chunk INDEX, not the chunk object.
    //
    // `assignment` is rebuilt by the parent on every render, so `subChunks`
    // — and therefore `chunk` — is a fresh reference each time. Depending on
    // the object re-ran this effect on any unrelated re-render (the 8s
    // heartbeat, a toast, a room update): the cleanup cancelled the in-flight
    // take, a second one started, and if the first had already uploaded, the
    // duplicate submit was rejected with "that sub-chunk is already recorded"
    // — mid-performance, with the take lost.
    //
    // A number cannot change identity, so a re-render can no longer interrupt
    // a live recording. `advance`, `auth` and `round` are deliberately out:
    // they are read at call time and re-running on them means the same bug.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, chunk?.index]);

  /**
   * The one place the whole recording loop can die on iOS Safari.
   *
   * Both gesture-gated calls — `video.play()` and the AudioContext resume —
   * must be *initiated* synchronously inside this tap, before the first
   * `await`. Once awaited, the user-activation flag is gone and Safari
   * rejects them. So: fire both, keep the promises, and only then start
   * awaiting.
   *
   * The priming play is muted because a muted play is always permitted. It
   * is what marks the element as user-unlocked, which is what lets the
   * refresher effect later play it UNMUTED from a non-gesture context.
   */
  function begin() {
    const video = videoRef.current;
    if (!video) return;

    video.muted = true;
    const videoUnlocked = video.play();
    const audioUnlocked = media.unlock();

    void (async () => {
      try {
        await videoUnlocked;
        video.pause();
        video.currentTime = chunks[0]?.start ?? 0;
      } catch {
        onToast("This browser blocked video playback. Tap the video, then start.", "error");
        return;
      }

      try {
        await audioUnlocked;
        await media.armMic();
      } catch {
        onToast("Microphone access is required to dub. Check your browser settings.", "error");
        return;
      }

      setArmed(true);
      setStage("refresher");
    })();
  }

  async function startRedo(chunkIndex: number) {
    try {
      // Server-side spend first: if the redo is already gone we never get as
      // far as arming the take.
      await dubs.redo(auth, round, chunkIndex);
      redoTarget.current = chunkIndex;
      setPickingRedo(false);
      setIndex(chunkIndex);
      setStage("refresher");
    } catch (err) {
      onToast(errorText(err, "No redo left."), "error");
    }
  }

  const banner = BANNER[stage];
  const redoLeft = assignment.redosRemaining;
  const allDone = recorded.length >= chunks.length;

  return (
    <div className="bd-screen">
      <NavBar
        subnav={
          <>
            <span>Round {round}</span>
            <span aria-hidden="true">&middot;</span>
            <span>Someone else&rsquo;s clip</span>
            <span aria-hidden="true">&middot;</span>
            <span>
              Chunk {Math.min(index + 1, chunks.length)}/{chunks.length}
            </span>
          </>
        }
      />

      <main className="shell bd-stack-lg">
        {/* The banner is the whole UI when you are performing — it has to read
            from across a room, so it is full-bleed and colour-coded. */}
        <div
          className={cx(
            "bd-banner",
            "t-nav",
            banner.tone,
            stage === "recording" && "bd-banner-live",
          )}
          role="status"
          aria-live="assertive"
        >
          {stage === "recording" ? (
            <span className="bd-rec-dot bd-rec-pulse" aria-hidden="true" />
          ) : null}
          {banner.text}
        </div>

        <div className="bd-stage">
          <video
            ref={videoRef}
            className="bd-stage-media"
            src={assignment.clipUrl}
            playsInline
            preload="auto"
          />
          <CountdownOverlay value={count} caption="Recording starts" />
          {stage === "idle" ? (
            <div className="bd-stage-empty t-body">
              <div className="bd-center-col">
                <p className="t-label">{chunks.length} sub-chunks to perform</p>
                <Button
                  variant="submit"
                  beacon
                  disabled={!assignment.clipUrl}
                  onClick={begin}
                >
                  {assignment.clipUrl ? "Arm mic & start" : "Loading clip…"}
                </Button>
                <p className="t-micro">
                  You&rsquo;ll watch each bit once with sound, then re-voice it muted.
                </p>
              </div>
            </div>
          ) : null}
        </div>

        <Panel title="Progress" animate={false}>
          <div className="bd-dots" aria-hidden="true">
            {chunks.map((c) => (
              <span
                key={c.index}
                className={cx(
                  "bd-dot",
                  recorded.includes(c.index) && "is-done bd-dot-fill",
                  c.index === index && stage !== "done" && stage !== "idle" && "is-active",
                )}
              />
            ))}
          </div>
          <p className="bd-sr-only" role="status">
            {recorded.length} of {chunks.length} sub-chunks recorded.
          </p>

          <hr className="dotted-divider" />

          <MicLevel level={level} armed={armed} />

          <hr className="dotted-divider" />

          <div className="spread">
            <Badge tone={redoLeft > 0 ? "amber" : "carbon"}>
              {redoLeft > 0 ? `${redoLeft} redo left` : "Redo spent"}
            </Badge>
            <Button
              variant="primary"
              disabled={redoLeft <= 0 || recorded.length === 0 || stage !== "done"}
              onClick={() => setPickingRedo((v) => !v)}
            >
              {pickingRedo ? "Cancel redo" : "Redo a sub-chunk"}
            </Button>
          </div>

          {pickingRedo ? (
            <div className="bd-chunk-picks bd-pop">
              {chunks.map((c) => (
                <button
                  key={c.index}
                  type="button"
                  className="bd-chunk-pick chip"
                  disabled={!recorded.includes(c.index)}
                  onClick={() => void startRedo(c.index)}
                >
                  #{c.index + 1}
                </button>
              ))}
            </div>
          ) : null}
        </Panel>

        {stage === "done" && allDone ? (
          <div className="plate chamfer bd-pad-lg center bd-pop bd-center-col">
            <p className="t-label">You&rsquo;re done</p>
            {canControl ? (
              <>
                <p className="t-body muted">
                  Move the room on whenever everyone has finished. Anyone still
                  recording gets skipped.
                </p>
                {hostAbsent ? <HostAbsentNotice /> : null}
                <Button
                  variant="submit"
                  block
                  beacon
                  disabled={advancing}
                  onClick={() => void goPlayback()}
                >
                  {advancing ? "Starting…" : "Play the dubs"}
                </Button>
              </>
            ) : (
              <p className="t-body muted">
                Waiting for everyone else to finish. The host moves the room on.
              </p>
            )}
          </div>
        ) : null}
      </main>

      <Footer />
    </div>
  );
}
